import { useContext } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import { Image, ModalContext, SidebarModal } from '@domain-group/suit-theme'

import { generateUrls as generateCampaignUrls } from '@hooks/useCampaign'
import { generateUrls as generateCampaignContactUrls } from '@hooks/useCampaignContact'
import useCampaignContacts from '@hooks/useCampaignContacts'
import { generateUrls as generateOrderUrls } from '@hooks/useOrder'
import { generateUrls as generateCampaignQuoteUrls } from '@hooks/useCampaignQuote'
import useCurrentUser from '@hooks/useCurrentUser'

import type { CampaignModel } from '@models/types'

import { getCampaignPermissions, getCampaignType } from 'shared/functions/campaign'

import AccountingItemsImageUrl from './assets/account-items.svg'
import ContactImageUrl from './assets/contact.svg'
import OrderImageUrl from './assets/order.svg'
import QuoteImageUrl from './assets/quote.svg'

const MODAL_KEY = 'NewCampaignItemModal'

type BuildCampaignQuoteURLParams = {
  campaign: CampaignModel,
  hasFilteredCampaignContacts: boolean,
}

function buildCampaignQuoteURL(params: BuildCampaignQuoteURLParams) {
  const { campaign: { end_date, start_date, id }, hasFilteredCampaignContacts } = params

  const {
    addCampaignQuoteUrl,
    campaignQuotesAddContactUrl,
    campaignQuotesEditDatesUrl,
  } = generateCampaignQuoteUrls({ campaign_id: id })

  if (!start_date && !end_date) return campaignQuotesEditDatesUrl
  if (!hasFilteredCampaignContacts && !!start_date && !!end_date) return campaignQuotesAddContactUrl
  return addCampaignQuoteUrl
}

type ModalCallbacks = {
  closeModal: () => void,
}

interface ModalProps {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const NewCampaignItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign, campaign: { id: campaignId } } = modalPayload

  const currentUserPayload = useCurrentUser()
  const { currentUser } = currentUserPayload

  const permissions = getCampaignPermissions({
    user: currentUser,
    campaignType: getCampaignType(campaign),
  })

  const canManageOrders = permissions.orders.manage || false
  const quotesRequired = permissions.orders.require_quote || false
  const canManageQuotes = permissions.quotes.manage || false
  const canManageContacts = permissions.contacts.manage || false
  const canManageInvoices = permissions.invoices.manage || false

  const campaignContactsPayload = useCampaignContacts({
    filters: { campaign_id: campaignId },
    performHttpRequests: !!campaignId,
    requestOptions: { include_campaign_contact_contact: true },
  })
  const { hasFilteredCampaignContacts } = campaignContactsPayload

  const {
    addCampaignAccountingItemUrl,
  } = generateCampaignUrls(campaign)

  const generateUrlsParams = {
    campaign_id: campaign.id,
    agency_id: campaign.agency_id,
  }

  const { addCampaignContactUrl } = generateCampaignContactUrls(generateUrlsParams)

  const { addCampaignOrderUrl } = generateOrderUrls(generateUrlsParams)

  return (
    <SidebarModal callbacks={callbacks} modalKey={MODAL_KEY} showModal={showModal} size="small">

      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Item" />

      <SidebarModal.Content hasFooter={false}>
        {canManageOrders && !quotesRequired && (
          <SidebarModal.Button
            href={addCampaignOrderUrl}
            iconLeft={<Image backgroundSize="contain" height={40} width={40} url={OrderImageUrl} />}
            showAngleIcon
            subtitle="Start a campaign order"
            title="Create Order"
          />
        )}

        {canManageQuotes && (
          <SidebarModal.Button
            href={buildCampaignQuoteURL({ campaign, hasFilteredCampaignContacts })}
            iconLeft={<Image backgroundSize="contain" height={40} width={40} url={QuoteImageUrl} />}
            showAngleIcon
            subtitle="Start quote from template or new"
            title="Create Quote"
          />
        )}

        {canManageContacts && (
          <SidebarModal.Button
            href={addCampaignContactUrl}
            iconLeft={<Image backgroundSize="contain" height={40} width={40} url={ContactImageUrl} />}
            showAngleIcon
            subtitle="Add a campaign contact"
            title="Create Contact"
          />
        )}

        {canManageInvoices && (
          <SidebarModal.Button
            href={addCampaignAccountingItemUrl}
            iconLeft={<Image backgroundSize="contain" height={40} width={40} url={AccountingItemsImageUrl} />}
            showAngleIcon
            subtitle="Invoices, Credits, Payments etc."
            title="Accounting Items"
          />
        )}
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <NewCampaignItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
